import { UserInfo } from '@user/userInfo.js';

/**
 * Returns object with all games and timestamp from localstorage
 * @returns {Object}
 */
export async function getAllGames(bypass = false) {
  const moduleBasicFunc = await import('../tools/basic-functions.js');
  const localStorageItemName = _CMS_MOBILE ? _CMS_USERIP + '-allGamesM' : _CMS_USERIP + '-allGamesD',
    device = _CMS_MOBILE ? 'mobile' : 'desktop',
    jsonKey = `api/game-collections-v2/all-games/?country=${_CMS_USERIP}&device=${device}`,
    jsonUrl = moduleBasicFunc.getWalletURL(jsonKey);
  if (moduleBasicFunc.checkLocalStorage(localStorageItemName, bypass ? 1 : 60 * 24 * 30)) {
    if (bypass) return true;
    var data = moduleBasicFunc.getFromLocalStorage(localStorageItemName);
  } else {
    const isActive = await moduleBasicFunc.startFetch(localStorageItemName);
    if (isActive) return await getAllGames(bypass);
    var data = await moduleBasicFunc.getJSON(jsonUrl);
    moduleBasicFunc.stopFetch(localStorageItemName);
    if (data.items) {
      data = { games: data.items, timestamp: new Date().getTime() };
      moduleBasicFunc.setLocalStorageItem(localStorageItemName, data);
    } else {
      try {
        data = JSON.parse(localStorage.getItem(localStorageItemName));
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        data = { games: [] };
      }
    }
  }
  if (!data.games) {
    return false;
  }
  if (bypass) return false;
  if (_CMS_USERIP != 'pl' || document.body.classList.contains('bf-bypass')) return data.games;
  return data.games.filter(({ provider }) => provider != 'bf games' && provider != 'bf-games');
}

/**
 * Returns object with games collections
 * @returns {Object}
 */
export async function getGamesCollections(bypass = false) {
  const moduleBasicFunc = await import('../tools/basic-functions.js');
  const localStorageItemName = _CMS_MOBILE ? _CMS_USERIP + '-gamesCollectionsM' : _CMS_USERIP + '-gamesCollectionsD',
    device = _CMS_MOBILE ? 'mobile' : 'desktop',
    jsonKey = `api/game-collections-v2/?country=${_CMS_USERIP}&device=${device}`,
    jsonUrl = moduleBasicFunc.getWalletURL(jsonKey);
  if (moduleBasicFunc.checkLocalStorage(localStorageItemName, bypass ? 1 : 60 * 24 * 30)) {
    if (bypass) return true;
    return moduleBasicFunc.getFromLocalStorage(localStorageItemName).collections;
  } else {
    const isActive = await moduleBasicFunc.startFetch(localStorageItemName);
    if (isActive) return await getGamesCollections(bypass);
    var data = await moduleBasicFunc.getJSON(jsonUrl);
    moduleBasicFunc.stopFetch(localStorageItemName);
    if (data?.length > 0) {
      data = { collections: data, timestamp: new Date().getTime() };
      moduleBasicFunc.setLocalStorageItem(localStorageItemName, data);
    } else {
      try {
        data = JSON.parse(localStorage.getItem(localStorageItemName));
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        data = { games: [] };
      }
    }
  }
  if (bypass) return false;
  return data.collections;
}

export async function getLastPlayedGamesCollections() {
  const userInfoInstance = new UserInfo();
  const isLogged = await userInfoInstance.isLoggedAsync();
  if (!isLogged) {
    return false;
  }

  const moduleBasicFunc = await import('../tools/basic-functions.js');
  const jsonKey = `api/user/last-games/`,
    jsonUrl = moduleBasicFunc.getWalletURL(jsonKey);
  let data = await moduleBasicFunc.getJSON(jsonUrl, undefined, [403]);
  if (!data || data.length == 0) return false;
  data = data.map((game) => {
    return game.id;
  });
  return { items: data };
}

export async function getFavoriteGamesCollections() {
  const userInfoInstance = new UserInfo();
  const isLogged = await userInfoInstance.isLoggedAsync();
  if (!isLogged) {
    return [];
  }

  const jsonKey = 'user/get_favorite_games/',
    key = 'favourite-games';
  if (window[key]) return window[key];
  const moduleBasicFunc = await import('../tools/basic-functions.js');
  const jsonUrl = moduleBasicFunc.getWalletURL(jsonKey, 'user'),
    data = await moduleBasicFunc.getJSON(jsonUrl, undefined, [404]);
  if (!data?.games) return { items: [] };
  const items = { items: data.games.map((item) => (item = parseInt(item))) };
  window[key] = items;
  return items;
}

export async function getSelectedGames() {
  try {
    if (_CMS_USER_SESSION_ACTIVE != '1') {
      return false;
    }

    const prioritizeBoostedGames = (games, boosted) => {
      const boostedGames = games.filter(({ tags }) => tags.includes(boosted));

      if (boostedGames.length >= 2) {
        const gamesToMove = boostedGames.splice(0, 2);

        if (games.slice(0, 6).filter((game) => gamesToMove.includes(game)).length < 2) {
          games = games.filter((game) => !gamesToMove.includes(game));

          gamesToMove.forEach((game, index) => {
            games.splice(Math.floor(Math.random() * 3) + 3 * index, 0, game);
          });
        }
      }

      return games;
    };

    /*
     * function logic
     */

    // Create a cache to store filtered games by tags
    const tagGameCache = new Map();

    // Create a cache for games by provider
    //const providerGameCache = {};

    const [lastPlayedGamesIds, allGames] = await Promise.all([getLastPlayedGamesCollections(), getAllGames()]);

    if (!lastPlayedGamesIds?.items || !allGames) {
      return { items: [] };
    }

    const { boosted, blocked } = (await getSelectedGames_Tags()) || {};

    const { removeGamesIfNotInArraySortDefault } = await import('./games.js');

    let lastPlayedGames = removeGamesIfNotInArraySortDefault(allGames, lastPlayedGamesIds.items);

    if (!lastPlayedGames.length) return { items: [] };

    // Filtering out games with blocked tags just once
    const validGames = allGames.filter((game) => !game.tags.some((tag) => blocked.includes(tag)));

    // Create tag map more efficiently
    const tagMap = new Map();
    lastPlayedGames.forEach(({ tags }) => {
      tags.forEach((tag) => {
        if (!blocked.includes(tag)) {
          if (!tagGameCache.has(tag)) {
            const games = validGames.filter((game) => game.tags.includes(tag));
            tagGameCache.set(tag, games.slice(0, 20));
          }
          tagMap.set(tag, tagGameCache.get(tag));
        }
      });
    });

    const providerTagGameCache = {};

    Array.from(tagMap.values()).forEach((gamesArray) => {
      gamesArray.forEach((game) => {
        const provider = game.provider;
        if (!providerTagGameCache[provider]) providerTagGameCache[provider] = [];
        providerTagGameCache[provider].push(game);
      });
    });

    const providersFromTagMap = Object.keys(providerTagGameCache);

    let totalGamesInTagMap = Array.from(tagMap.values()).flat().length;
    let selectedGames = [];

    for (let i = 0; i < totalGamesInTagMap; i++) {
      const provider = providersFromTagMap[i % providersFromTagMap.length];
      const game = providerTagGameCache[provider].shift();
      if (game && !lastPlayedGamesIds.items.includes(game.id)) selectedGames.push(game);
    }

    // Prioritize boosted games

    if (boosted) {
      selectedGames = prioritizeBoostedGames(selectedGames, boosted);
    }

    return {
      items: [...new Set(selectedGames.map(({ id }) => id))].splice(0, 24)
    };
  } catch (error) {
    console.error('An error occurred:', error);
    return { items: [] };
  }
}

export async function getSelectedGames_Tags() {
  const { getAPIURL, getJSON } = await import('../tools/basic-functions.js');
  const jsonKey = `${_CMS_LANGUAGE}/json/selected-for-you-tags.json`,
    jsonUrl = getAPIURL(jsonKey, null, true);
  var data = await getJSON(jsonUrl);
  return data;
}

export async function getGamesData(slug) {
  const { getAPIURL, getJSON } = await import('../tools/basic-functions.js');
  const jsonKey = `${_CMS_LANGUAGE}/json/games-data/${slug}`,
    jsonUrl = getAPIURL(jsonKey, null, true);
  var data = await getJSON(jsonUrl);
  return data;
}
